/**
 * 会话 token 的**唯一存放处** —— 而且存的是**两个**。
 *
 * ### 为什么要两处
 *
 * 2026-10-07 拆表之后有两套身份：会员（工作台）与后台账号（`/admin`）。
 * 它们是**两个独立会话**（后端 `sessions.principal` 区分），所以前端也得
 * 各存一个 token：
 *
 *   · 同时开着工作台与后台是**合法状态**（两边都要能刷余额 / 拉列表）；
 *   · 退出后台**不许**把工作台踢下线，反之亦然。
 *
 * 用一个 token 轮着用的话，进一趟后台就会顶掉工作台的登录态，
 * 而用户只会看到「我去后台看一眼，回来怎么要重新登录」。
 *
 * ### 为什么单开一个文件
 *
 * token 有**三个**读写方 —— `client.ts`（每个请求都要带上）、两个 Context
 * （登录写入 / 退出清除）、以及 401 时的兜底清除。三处各写一遍
 * `localStorage.getItem('hw.token')`，键名改一次就必然漏一处，
 * 而漏掉的那处**不会报错**：token 静静地不再被读出，界面表现是「登录了但一直掉线」。
 *
 * ### 请求该带哪个 token
 *
 * **由路径决定，不由调用方决定**（见 `client.ts` 的 `principalOf`）——
 * 让每个 API 方法自己声明「我是哪一侧的」，迟早会漏一个，
 * 而漏掉的表现是「某个后台接口拿会员 token 打过去，然后 401」。
 *
 * 顺带说明为什么用 localStorage 而不是内存：内存态一刷新就没了，
 * 用户每次刷新页面都要重新登录一遍。
 */

/** 两套身份。与后端 `sessions.principal` 的字面量逐字一致。 */
export type Principal = 'user' | 'admin'

const TOKEN_KEY = 'hw.token'
const ADMIN_TOKEN_KEY = 'hw.adminToken'

/** 两个键的**唯一映射表**。想加第三套身份，从这里加，别在别处写 if。 */
const KEYS: Record<Principal, string> = {
  user: TOKEN_KEY,
  admin: ADMIN_TOKEN_KEY,
}

function read(key: string): string {
  try {
    return localStorage.getItem(key) ?? ''
  } catch {
    // 隐私模式 / 禁用 storage：当作没登录，不要让它把整个应用炸掉。
    return ''
  }
}

function write(key: string, token: string): void {
  try {
    if (token) localStorage.setItem(key, token)
    else localStorage.removeItem(key)
  } catch {
    /* 同上，存不下就只在内存里活着（Context 自己持有账号，功能不受影响） */
  }
}

/** 读某一侧的 token。没有就返回空串（**不返回 null**，少一层判空）。 */
export function tokenOf(principal: Principal): string {
  return read(KEYS[principal])
}

/** 清掉某一侧的 token。**只清这一侧** —— 这是「两套登录态」的全部意义。 */
export function clearTokenOf(principal: Principal): void {
  write(KEYS[principal], '')
}

/** 会员侧 token。 */
export function getToken(): string {
  return read(TOKEN_KEY)
}

export function setToken(token: string): void {
  write(TOKEN_KEY, token)
}

export function clearToken(): void {
  clearTokenOf('user')
}

/** 后台侧 token。**与会员侧互不影响**。 */
export function getAdminToken(): string {
  return read(ADMIN_TOKEN_KEY)
}

export function setAdminToken(token: string): void {
  write(ADMIN_TOKEN_KEY, token)
}

export function clearAdminToken(): void {
  clearTokenOf('admin')
}

/**
 * 会话失效广播。
 *
 * 谁发：`client.ts` —— 任何请求收到 401 时。
 * 谁收：两个 Context —— 各自只关心**自己那一侧**的广播。
 *
 * 🔴 **必须带身份**。不带的话，「后台 token 过期」会把工作台也一起清掉，
 * 而用户正在工作台干活 —— 他看到的是一次毫无理由的掉线。
 * （反过来也一样：工作台过期顺手清掉后台 token，切过去就得重新登。）
 *
 * 没有这条线的话，token 过期后界面会**停在原地**：顶栏还挂着用户名和余额，
 * 每个请求各自报「登录已过期」，用户不知道该做什么。
 */
export const UNAUTHORIZED_EVENT = 'hw:unauthorized'

/** 收到广播的那一侧。用 event 的 `detail` 传，而不是再定义两个事件名 —— 见上面那条。 */
export function emitUnauthorized(principal: Principal): void {
  try {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: principal }))
  } catch {
    /* 非浏览器环境（冒烟脚本）忽略 */
  }
}

/** 判断一条广播是不是发给自己的。 */
export function isUnauthorizedFor(event: Event, principal: Principal): boolean {
  return (event as CustomEvent).detail === principal
}
